import { Trans } from '@lingui/react/macro';
import '@warp-ds/components/icon';
import { Alert, Button, Modal } from '@warp-ds/react';
import { useState } from 'react';
import type { DescriptionVariant } from '../../types/ai-description.js';
import { getHuckConfig } from '../utils/getHuckConfig.js';

interface AiDescriptionModalProps {
  isOpen: boolean;
  suggestions: DescriptionVariant[];
  onClose: () => void;
  onSelect: (suggestion: DescriptionVariant) => void;
  hasExistingContent: boolean;
}

export const AiDescriptionModal = ({
  isOpen,
  suggestions,
  onClose,
  onSelect,
  hasExistingContent,
}: AiDescriptionModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { locale } = getHuckConfig();

  if (!isOpen || suggestions.length === 0) {
    return null;
  }

  const currentSuggestion = suggestions[currentIndex];

  const handleGenerateNew = () => {
    setCurrentIndex((prev) => (prev + 1) % suggestions.length);
  };

  const handleUse = () => {
    onSelect(currentSuggestion);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onDismiss={onClose}
      title={
        <h2>
          <Trans
            id="mobility-item-insertion.form.ai-description.modal-title"
            comment="Title for AI description suggestions modal"
          >
            Suggested description
          </Trans>
        </h2>
      }
      footer={
        <div className="flex gap-12 justify-end">
          <Button onClick={onClose}>
            <Trans id="mobility-item-insertion.form.ai-description.cancel" comment="Cancel button">
              Cancel
            </Trans>
          </Button>
          <Button primary onClick={handleUse}>
            <Trans id="mobility-item-insertion.form.ai-description.use-suggestion" comment="Use suggestion button">
              Use suggestion
            </Trans>
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-16">
        <div className="p-16 rounded-8 border border-subtle">
          <p className="s-text text-m leading-m whitespace-pre-wrap">{currentSuggestion.text}</p>
          <Button link onClick={handleGenerateNew} className="self-start" disabled={suggestions.length === 1}>
            <div className="flex items-center justify-center gap-8">
              <w-icon name="Refresh" size="small" locale={locale} />
              <span className="font-bold">
                <Trans
                  id="mobility-item-insertion.form.ai-description.generate-new"
                  comment="Button to show next suggestion"
                >
                  Generate new
                </Trans>
              </span>
            </div>
          </Button>
        </div>
        <p className="s-text text-s text-subtle">
          <Trans
            id="mobility-item-insertion.form.ai-description.edit-hint"
            comment="Helper text explaining user can edit later"
          >
            You can edit the text in the next step
          </Trans>
        </p>
        {hasExistingContent && (
          <Alert type="warning" show>
            <Trans
              id="mobility-item-insertion.form.ai-description.replace-warning"
              comment="Warning that using suggestion will replace existing text"
            >
              Using this suggestion will replace your current description.
            </Trans>
          </Alert>
        )}
      </div>
    </Modal>
  );
};
